/**
 * Deletion Wall
 * The left edge of doom - anything touching it is erased
 * Features static/noise effect and houses the Overfitter
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';

export class DeletionWall {
    constructor(scene) {
        this.scene = scene;
        this.width = 60;
        this.noiseTime = 0;
        this.intensity = 0.3;

        // Create graphics for the wall
        this.graphics = scene.add.graphics();
        this.graphics.setDepth(50);

        // Create noise texture data
        this.noiseCanvas = document.createElement('canvas');
        this.noiseCanvas.width = this.width;
        this.noiseCanvas.height = this.scene.scale.height;
        this.noiseCtx = this.noiseCanvas.getContext('2d');

        // Warning glow that extends from wall
        this.glowGraphics = scene.add.graphics();
        this.glowGraphics.setDepth(49);

        // Tendrils reaching out from the wall
        this.tendrils = [];
        this.tendrilTime = 0;

        this.createTendrils();
    }

    createTendrils() {
        // Create several tendril objects
        for (let i = 0; i < 5; i++) {
            this.tendrils.push({
                baseY: 100 + i * 100,
                length: 20 + Math.random() * 30,
                speed: 0.5 + Math.random() * 0.5,
                phase: Math.random() * Math.PI * 2,
                thickness: 2 + Math.random() * 3
            });
        }
    }

    update(delta, cameraX, playerX, playerY) {
        this.noiseTime += delta;
        this.tendrilTime += delta * 0.001;

        const wallX = cameraX;

        // Calculate player proximity (0 = at wall, 1 = far from wall)
        const distanceFromWall = playerX - wallX;
        const maxSafeDistance = 300;
        this.proximity = 1 - Math.min(distanceFromWall / maxSafeDistance, 1);
        this.proximity = Math.max(0, this.proximity);

        // Intensity increases as player gets closer
        this.intensity = 0.3 + this.proximity * 0.7;

        // Draw the wall
        this.drawWall(wallX);
        this.drawGlow(wallX);
        this.drawTendrils(wallX, playerY);

        return this.proximity;
    }

    drawWall(wallX) {
        this.graphics.clear();

        // Wall draws FROM the left edge of camera INTO the screen
        const drawX = wallX; // Start at left edge of visible area

        // Generate noise pattern
        const imageData = this.noiseCtx.createImageData(this.width, this.scene.scale.height);
        const data = imageData.data;

        for (let y = 0; y < this.scene.scale.height; y++) {
            for (let x = 0; x < this.width; x++) {
                const i = (y * this.width + x) * 4;

                // Static noise with time variation
                const noise = Math.random();
                const timeWave = Math.sin(this.noiseTime * 0.01 + y * 0.1) * 0.2;

                // Color based on intensity (darker = more dangerous)
                const brightness = noise * (50 + timeWave * 20) * this.intensity;

                // Occasional red flicker
                const redFlicker = Math.random() > 0.98 ? 100 : 0;

                data[i] = brightness * 0.3 + redFlicker;     // R
                data[i + 1] = brightness * 0.1;               // G
                data[i + 2] = brightness * 0.4;               // B
                data[i + 3] = 200 + this.intensity * 55;      // A
            }
        }

        this.noiseCtx.putImageData(imageData, 0, 0);

        // Draw to Phaser graphics
        // Since we can't easily use canvas texture dynamically, draw rectangles
        for (let y = 0; y < this.scene.scale.height; y += 4) {
            for (let x = 0; x < this.width; x += 4) {
                const noise = Math.random();
                const brightness = noise * this.intensity;
                const redTint = Math.random() > 0.95 ? 0.5 : 0;

                const color = Phaser.Display.Color.GetColor(
                    Math.floor((brightness * 0.3 + redTint) * 255),
                    Math.floor(brightness * 0.1 * 255),
                    Math.floor(brightness * 0.4 * 255)
                );

                this.graphics.fillStyle(color, 0.8 + this.intensity * 0.2);
                this.graphics.fillRect(wallX + x, y, 4, 4); // Draw INTO the screen
            }
        }

        // Solid black core at the very left edge
        this.graphics.fillStyle(0x000000, 1);
        this.graphics.fillRect(wallX, 0, 15, this.scene.scale.height);

        // Pulsing red edge (at the right side of the wall)
        const pulse = Math.sin(this.noiseTime * 0.005) * 0.3 + 0.7;
        this.graphics.fillStyle(0x660000, pulse * this.intensity);
        this.graphics.fillRect(wallX + this.width - 5, 0, 8, this.scene.scale.height);
    }

    drawGlow(wallX) {
        this.glowGraphics.clear();

        // Warning gradient extending from wall's right edge
        const glowStartX = wallX + this.width;
        const glowWidth = 100 + this.proximity * 50;

        for (let i = 0; i < 10; i++) {
            const alpha = (1 - i / 10) * 0.15 * this.intensity;
            const width = glowWidth * (i / 10);

            this.glowGraphics.fillStyle(0x220000, alpha);
            this.glowGraphics.fillRect(glowStartX, 0, width, this.scene.scale.height);
        }

        // Pulsing danger indicator
        if (this.proximity > 0.5) {
            const dangerPulse = Math.sin(this.noiseTime * 0.01) * 0.5 + 0.5;
            this.glowGraphics.fillStyle(0x440000, dangerPulse * this.proximity * 0.3);
            this.glowGraphics.fillRect(glowStartX, 0, 150, this.scene.scale.height);
        }
    }

    drawTendrils(wallX, playerY) {
        // Dark tendrils reaching out from the wall's right edge
        const tendrilStartX = wallX + this.width;

        this.tendrils.forEach((tendril, index) => {
            // Tendrils reach toward player Y position
            const targetY = playerY;
            const yOffset = Math.sin(this.tendrilTime * tendril.speed + tendril.phase) * 30;
            const reachY = tendril.baseY + (targetY - tendril.baseY) * 0.3 + yOffset;

            // Length increases with proximity
            const currentLength = tendril.length * (1 + this.proximity * 2);

            // Draw wavy tendril
            this.graphics.lineStyle(tendril.thickness, 0x330808, 0.6 + this.proximity * 0.4);
            this.graphics.beginPath();
            this.graphics.moveTo(tendrilStartX, tendril.baseY);

            const segments = 5;
            for (let i = 1; i <= segments; i++) {
                const t = i / segments;
                const x = tendrilStartX + currentLength * t;
                const waveY = Math.sin(this.tendrilTime * 2 + i + index) * 10 * t;
                const y = Phaser.Math.Linear(tendril.baseY, reachY, t) + waveY;
                this.graphics.lineTo(x, y);
            }

            this.graphics.strokePath();
        });
    }

    // Check if a point is inside the deletion zone
    isInDeletionZone(x, y, cameraX) {
        return x <= cameraX + 10;
    }

    // Get the current threat level (0-1)
    getThreatLevel() {
        return this.proximity;
    }

    destroy() {
        this.graphics.destroy();
        this.glowGraphics.destroy();
    }
}
